/* oxlint-disable typescript/consistent-type-assertions -- lint to the future batch ignore */
import { useEmberService } from '@qonto/react-migration-toolkit/react/hooks';
import { useEmberIntl } from '@qonto/react-migration-toolkit/react/hooks/providers';
import { type PolymorphicSegment, PolymorphicSegmentProvider } from '@repo/poly-hooks/segment';
import { type Session, SessionProvider } from '@repo/poly-hooks/session';
import { ConfigProvider } from '@repo/shared-kit/config/config-provider';
import { getEnvironmentVariables } from '@repo/shared-config/app/helper/environment-variables';
import { SentryProvider } from '@repo/shared-kit/sentry/context';
import { ZendeskLocalizationProvider } from '@repo/poly-hooks/zendesk-localization';
import { QueryClientProvider } from '@tanstack/react-query';
import { type PropsWithChildren, type ReactNode, useMemo } from 'react';
import { I18nProvider } from 'react-aria';
import { IntlProvider } from 'react-intl';
import { defaultRichTextElements } from '@repo/shared-kit/utils/intl';
import { NavigateProvider } from '@repo/poly-hooks/routing';
import { useEmberNavigate } from '@repo/poly-hooks/routing/-ember/get-navigate';
import { FlagsProvider } from '@repo/poly-hooks/flags';
import { createEmberSentryAdapter } from 'register-partners-js/utils/sentry-adapter';
import { createEmberZendeskLocalizationAdapter } from 'register-partners-js/utils/zendesk-localization-adapter';
import { fromEmberConfig } from 'register-partners-js/react/config/adapter';

export function CustomProviders({ children }: PropsWithChildren): ReactNode {
  const intl = useEmberIntl();
  const segment = useEmberService('segment');
  const appConfig = useMemo(() => fromEmberConfig(getEnvironmentVariables()), []);
  const navigate = useEmberNavigate();
  const launchdarkly = useEmberService('launchdarkly');
  const session = useEmberService('session');
  const sessionAdapter = useMemo(() => session as unknown as Session, [session]);
  const queryClientManager = useEmberService('queryClientManager');
  const sentry = useEmberService('sentry');
  const sentryAdapter = useMemo(() => createEmberSentryAdapter(sentry), [sentry]);
  const zendeskLocalizationService = useEmberService('zendesk-localization');
  const zendeskLocalization = useMemo(
    () => createEmberZendeskLocalizationAdapter(zendeskLocalizationService),
    [zendeskLocalizationService]
  );

  // Since we only pass a 2-char locale react aria default to en-US
  // making the date format mm/dd/yyyy
  const reactAriaIntlLocale = intl.locale === 'en' ? 'en-GB' : intl.locale;

  return (
    <ConfigProvider config={appConfig}>
      <SentryProvider sentry={sentryAdapter}>
        <SessionProvider session={sessionAdapter}>
          <NavigateProvider navigate={navigate}>
            <PolymorphicSegmentProvider segment={segment as unknown as PolymorphicSegment}>
              <FlagsProvider flags={launchdarkly.allFlags}>
                <ZendeskLocalizationProvider zendeskLocalization={zendeskLocalization}>
                  <IntlProvider
                    defaultLocale={intl.defaultLocale}
                    defaultRichTextElements={defaultRichTextElements}
                    locale={intl.locale}
                    messages={intl.messages}
                    wrapRichTextChunksInFragment
                  >
                    <I18nProvider locale={reactAriaIntlLocale}>
                      <QueryClientProvider client={queryClientManager.queryClient}>
                        {children}
                      </QueryClientProvider>
                    </I18nProvider>
                  </IntlProvider>
                </ZendeskLocalizationProvider>
              </FlagsProvider>
            </PolymorphicSegmentProvider>
          </NavigateProvider>
        </SessionProvider>
      </SentryProvider>
    </ConfigProvider>
  );
}
